import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { ArrowUp, ArrowDown, Trash2, Upload, Mic, MicOff, RefreshCw, Image, X } from 'lucide-react';
import { useAssetUpload } from '../hooks/useAssetUpload';
import { useTranslation } from '../i18n/I18nContext';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

export default function BlockEditor({ block, index, onUpdate, onDelete, onMove, canMoveUp, canMoveDown }) {
  const { t } = useTranslation();
  const assetUpload = useAssetUpload();
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorder = React.useRef(null);
  const audioChunks = React.useRef([]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await assetUpload.uploadAsset('image', file);
    if (result) {
      onUpdate({ ...block.props, url: result.url });
    }
  };

  const handleAudioSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await assetUpload.uploadAsset('audio', file);
    if (result) {
      onUpdate({
        ...block.props,
        url: result.url,
        duration_s: result.duration || 0
      });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
      }

      mediaRecorder.current = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      audioChunks.current = [];

      mediaRecorder.current.addEventListener('dataavailable', (event) => {
        audioChunks.current.push(event.data);
      });

      mediaRecorder.current.addEventListener('stop', () => {
        const audioBlob = new Blob(audioChunks.current, { type: mimeType || 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
      });

      mediaRecorder.current.start();
      setRecording(true);
      toast.success('Recording started');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && recording) {
      mediaRecorder.current.stop();
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      setRecording(false);
      toast.success('Recording stopped');
    }
  };

  const handleRecordingUpload = async () => {
    if (!audioChunks.current.length) {
      toast.error('No audio to upload');
      return;
    }

    const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
    const file = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });

    const result = await assetUpload.uploadAsset('audio', file);
    if (result) {
      onUpdate({
        ...block.props,
        url: result.url,
        duration_s: result.duration || 0
      });
      setAudioUrl(null);
      audioChunks.current = [];
    }
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await assetUpload.uploadAsset('video', file);
    if (result) {
      onUpdate({
        ...block.props,
        mp4_url: result.url,
        poster: result.url, // Use video URL as poster for now
        duration_s: result.duration || 0
      });
    }
  };

  // Handle attached image upload for text blocks
  const handleAttachedImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await assetUpload.uploadAsset('image', file);
    if (result) {
      onUpdate({ ...block.props, attached_image: result.url });
      toast.success('Afbeelding toegevoegd');
    }
  };

  const removeAttachedImage = () => {
    onUpdate({ ...block.props, attached_image: null });
    toast.success('Afbeelding verwijderd');
  };

  const renderEditor = () => {
    switch (block.type) {
      case 'text':
        return (
          <div className="space-y-4">
            {/* Rich Text Editor */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Tekst inhoud
              </label>
              <RichTextEditor
                value={block.props.content || ''}
                onChange={(content) => onUpdate({ ...block.props, content })}
                placeholder="Voer je tekst in..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Gebruik de toolbar voor opmaak: vet, cursief, koppen, lijsten
              </p>
            </div>
            
            {/* Optional Attached Image */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                <Image className="h-4 w-4 inline mr-1" />
                Optionele afbeelding
              </label>
              
              {block.props.attached_image ? (
                <div className="relative">
                  <img 
                    src={block.props.attached_image} 
                    alt="Attached" 
                    className="w-full max-h-48 object-cover rounded border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={removeAttachedImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById(`text-image-upload-${block.id}`).click()}
                    disabled={assetUpload.uploading}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    {assetUpload.uploading ? 'Uploaden...' : 'Voeg afbeelding toe'}
                  </Button>
                  <input
                    id={`text-image-upload-${block.id}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAttachedImageSelect}
                  />
                </div>
              )}
              
              {assetUpload.uploading && (
                <div className="mt-2 space-y-1">
                  <Progress value={assetUpload.progress} className="h-2" />
                  <p className="text-xs text-gray-500 text-center">{assetUpload.progress}%</p>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-2">
                De afbeelding wordt boven de tekst getoond in de player
              </p>
            </div>
            
            {/* Minimum view time */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Minimale kijktijd (seconden)
              </label>
              <Input
                type="number"
                min="0"
                value={block.props.min_view_seconds || 0}
                onChange={(e) => onUpdate({ ...block.props, min_view_seconds: parseInt(e.target.value) || 0 })}
                placeholder="0 = geen beperking"
                className="w-32"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leerling moet dit aantal seconden wachten voordat &quot;Volgende&quot; actief wordt
              </p>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                data-testid={`image-url-${block.id}`}
                value={block.props.url}
                onChange={(e) => onUpdate({ ...block.props, url: e.target.value })}
                placeholder="Image URL or upload below"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById(`image-upload-${block.id}`).click()}
                disabled={assetUpload.uploading}
                data-testid={`image-upload-button-${block.id}`}
              >
                <Upload className="h-4 w-4 mr-1" />
                {assetUpload.uploading ? 'Uploading...' : 'Upload'}
              </Button>
              <input
                id={`image-upload-${block.id}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
            </div>
            
            {assetUpload.uploading && (
              <div className="space-y-1">
                <Progress value={assetUpload.progress} className="h-2" />
                <p className="text-xs text-gray-500 text-center">{assetUpload.progress}%</p>
              </div>
            )}
            
            {assetUpload.error && (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  <span>{assetUpload.error}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById(`image-upload-${block.id}`).click()}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            
            <Input
              data-testid={`image-caption-${block.id}`}
              value={block.props.caption}
              onChange={(e) => onUpdate({ ...block.props, caption: e.target.value })}
              placeholder="Caption (optional)"
            />
            
            {/* Minimum view time */}
            <div className="border-t pt-3">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Minimum view time (seconds)
              </label>
              <Input
                type="number"
                min="0"
                value={block.props.min_view_seconds || 0}
                onChange={(e) => onUpdate({ ...block.props, min_view_seconds: parseInt(e.target.value) || 0 })}
                placeholder="0 = no restriction"
                className="w-32"
              />
              <p className="text-xs text-gray-500 mt-1">
                Learner must wait this many seconds before clicking &quot;Next&quot;
              </p>
            </div>
            
            {block.props.url && (
              <div className="relative group">
                <img 
                  src={block.props.url} 
                  alt="Preview" 
                  className="w-full rounded border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    toast.error('Failed to load image');
                  }}
                />
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3">
            <div className="border-2 border-dashed rounded p-4 text-center">
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoSelect}
                className="hidden"
                id={`video-upload-${block.id}`}
                data-testid={`video-upload-${block.id}`}
                disabled={assetUpload.uploading}
              />
              <label htmlFor={`video-upload-${block.id}`} className="cursor-pointer">
                <div className="flex flex-col items-center">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {assetUpload.uploading
                      ? `Uploading... ${assetUpload.progress}%`
                      : 'Click to upload video (max 200MB)'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">9:16 vertical format recommended</p>
                </div>
              </label>
              
              {assetUpload.uploading && (
                <div className="mt-3 space-y-1">
                  <Progress value={assetUpload.progress} className="h-2" />
                  <p className="text-xs text-gray-500">{assetUpload.progress}%</p>
                </div>
              )}
            </div>
            
            {assetUpload.error && (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  <span>{assetUpload.error}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById(`video-upload-${block.id}`).click()}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            
            {block.props.mp4_url && (
              <div className="relative aspect-[9/16] max-w-xs mx-auto bg-black rounded overflow-hidden">
                <video 
                  src={block.props.mp4_url} 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  muted
                  preload="metadata"
                  crossOrigin="anonymous"
                  style={{ aspectRatio: '9/16' }}
                />
              </div>
            )}
            
            {/* Viewing Requirement Setting */}
            <div className="border-t pt-3 mt-3">
              <label className="block text-sm font-medium mb-2">{t('video_viewing_requirement')}</label>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border">
                <Switch
                  data-testid={`video-must-finish-${block.id}`}
                  checked={block.props.must_finish || false}
                  onCheckedChange={(checked) => onUpdate({ ...block.props, must_finish: checked })}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium">
                    {block.props.must_finish ? t('video_must_finish') : t('video_skippable')}
                  </span>
                  <p className="text-xs text-gray-500">
                    {block.props.must_finish ? t('video_must_finish_desc') : t('video_skippable_desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-3">
            <div className="border-2 border-dashed rounded p-4">
              <div className="flex gap-2 mb-3">
                {!recording ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startRecording}
                    disabled={assetUpload.uploading}
                    className="flex-1"
                    data-testid={`audio-record-button-${block.id}`}
                  >
                    <Mic className="h-4 w-4 mr-2" />
                    Start Recording
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={stopRecording}
                    className="flex-1"
                    data-testid={`audio-stop-button-${block.id}`}
                  >
                    <MicOff className="h-4 w-4 mr-2" />
                    Stop Recording
                  </Button>
                )}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById(`audio-upload-${block.id}`).click()}
                  disabled={assetUpload.uploading || recording}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload
                </Button>
                <input
                  id={`audio-upload-${block.id}`}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleAudioSelect}
                />
              </div>
              
              {audioUrl && !block.props.url && (
                <div className="space-y-2">
                  <audio src={audioUrl} controls className="w-full" />
                  <Button
                    type="button"
                    onClick={handleRecordingUpload}
                    disabled={assetUpload.uploading}
                    className="w-full"
                  >
                    {assetUpload.uploading ? 'Uploading...' : 'Save Recording'}
                  </Button>
                </div>
              )}
              
              {assetUpload.uploading && (
                <div className="mt-3 space-y-1">
                  <Progress value={assetUpload.progress} className="h-2" />
                  <p className="text-xs text-gray-500 text-center">{assetUpload.progress}%</p>
                </div>
              )}
              
              {assetUpload.error && (
                <Alert variant="destructive" className="mt-3">
                  <AlertDescription className="flex items-center justify-between">
                    <span>{assetUpload.error}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => document.getElementById(`audio-upload-${block.id}`).click()}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <Input
              data-testid={`audio-title-${block.id}`}
              value={block.props.title || ''}
              onChange={(e) => onUpdate({ ...block.props, title: e.target.value })}
              placeholder="Audio title (optional)"
            />
            
            <Textarea
              data-testid={`audio-description-${block.id}`}
              value={block.props.description || ''}
              onChange={(e) => onUpdate({ ...block.props, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
            />
            
            {block.props.url && (
              <div className="bg-gray-50 p-3 rounded">
                <audio src={block.props.url} controls className="w-full" />
                {block.props.duration_s > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    Duration: {Math.floor(block.props.duration_s / 60)}:{(block.props.duration_s % 60).toString().padStart(2, '0')}
                  </p>
                )}
              </div>
            )}
          </div>
        );

      case 'quiz': {
        // Determine if multiple answers are allowed (default: false = radio)
        const allowMultiple = block.props.allow_multiple || false;
        
        return (
          <div className="space-y-3">
            <Textarea
              data-testid={`quiz-question-${block.id}`}
              value={block.props.question}
              onChange={(e) => onUpdate({ ...block.props, question: e.target.value })}
              placeholder="Enter question"
              rows={2}
            />
            
            {/* Radio vs Checkbox toggle */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border">
              <Switch
                data-testid={`quiz-multiple-${block.id}`}
                checked={allowMultiple}
                onCheckedChange={(checked) => onUpdate({ ...block.props, allow_multiple: checked })}
              />
              <div className="flex-1">
                <span className="text-sm font-medium">{t('quiz_multiple_answers')}</span>
                <p className="text-xs text-gray-500">
                  {allowMultiple ? t('quiz_multiple_answer_hint') : t('quiz_single_answer_hint')}
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Choices:</label>
              {block.props.choices.map((choice, idx) => (
                <div key={choice.id} className="flex gap-2 items-center">
                  <Input
                    data-testid={`quiz-choice-${block.id}-${idx}`}
                    value={choice.text}
                    onChange={(e) => {
                      const newChoices = [...block.props.choices];
                      newChoices[idx].text = e.target.value;
                      onUpdate({ ...block.props, choices: newChoices });
                    }}
                    placeholder={`Choice ${idx + 1}`}
                  />
                  <Switch
                    data-testid={`quiz-correct-${block.id}-${idx}`}
                    checked={choice.correct}
                    onCheckedChange={(checked) => {
                      const newChoices = [...block.props.choices];
                      // For single answer (radio): uncheck all others first
                      if (!allowMultiple && checked) {
                        newChoices.forEach((c, i) => {
                          newChoices[i].correct = false;
                        });
                      }
                      newChoices[idx].correct = checked;
                      onUpdate({ ...block.props, choices: newChoices });
                    }}
                  />
                  <span className="text-xs text-gray-500">Correct</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const newChoices = block.props.choices.filter((_, i) => i !== idx);
                      onUpdate({ ...block.props, choices: newChoices });
                    }}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                    disabled={block.props.choices.length <= 2}
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onUpdate({
                    ...block.props,
                    choices: [...block.props.choices, { id: `${Date.now()}`, text: '', correct: false }]
                  });
                }}
                data-testid={`add-choice-${block.id}`}
              >
                Add Choice
              </Button>
            </div>
            
            {/* NEW: Correct answer explanation */}
            <div className="space-y-1 pt-2 border-t">
              <label className="text-sm font-medium text-green-700">
                {t('quiz_expl_correct_label')}
              </label>
              <Textarea
                data-testid={`quiz-correct-explanation-${block.id}`}
                value={block.props.correct_explanation || ''}
                onChange={(e) => onUpdate({ ...block.props, correct_explanation: e.target.value })}
                placeholder={t('quiz_expl_correct_placeholder')}
                rows={2}
                className="border-green-200 focus:border-green-400"
              />
            </div>
            
            {/* NEW: Incorrect answer explanation */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-amber-700">
                {t('quiz_expl_incorrect_label')}
              </label>
              <Textarea
                data-testid={`quiz-incorrect-explanation-${block.id}`}
                value={block.props.incorrect_explanation || ''}
                onChange={(e) => onUpdate({ ...block.props, incorrect_explanation: e.target.value })}
                placeholder={t('quiz_expl_incorrect_placeholder')}
                rows={2}
                className="border-amber-200 focus:border-amber-400"
              />
            </div>
            
            {/* Legacy explanation field - hidden if new fields are used */}
            {block.props.explanation && !block.props.correct_explanation && !block.props.incorrect_explanation && (
              <div className="space-y-1 opacity-60">
                <label className="text-sm font-medium text-gray-500">
                  {t('quiz_explanation_label')}
                </label>
                <Textarea
                  data-testid={`quiz-explanation-${block.id}`}
                  value={block.props.explanation}
                  onChange={(e) => onUpdate({ ...block.props, explanation: e.target.value })}
                  placeholder="Explanation (optional)"
                  rows={2}
                />
              </div>
            )}
            
            {/* Allow learner comment */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border">
              <Switch
                data-testid={`quiz-allow-comment-${block.id}`}
                checked={block.props.allow_comment || false}
                onCheckedChange={(checked) => onUpdate({ ...block.props, allow_comment: checked })}
              />
              <div className="flex-1">
                <span className="text-sm font-medium">Allow comment</span>
                <p className="text-xs text-gray-500">
                  Show an optional text field after the learner answers this question
                </p>
              </div>
            </div>
            
            {block.props.allow_comment && (
              <div className="space-y-1 pl-3 border-l-2 border-slate-200">
                <label className="text-sm font-medium text-gray-600">
                  Comment label (optional)
                </label>
                <Input
                  data-testid={`quiz-comment-label-${block.id}`}
                  value={block.props.comment_label || ''}
                  onChange={(e) => onUpdate({ ...block.props, comment_label: e.target.value })}
                  placeholder="Toelichting (optioneel)"
                />
                <p className="text-xs text-gray-400">
                  Custom label shown above the comment field. Leave empty for default.
                </p>
              </div>
            )}
          </div>
        );
      }

      case 'cta':
        return (
          <div className="space-y-3">
            <Textarea
              data-testid={`cta-text-${block.id}`}
              value={block.props.text}
              onChange={(e) => onUpdate({ ...block.props, text: e.target.value })}
              placeholder="CTA message"
              rows={2}
            />
            <Input
              data-testid={`cta-button-text-${block.id}`}
              value={block.props.button_text}
              onChange={(e) => onUpdate({ ...block.props, button_text: e.target.value })}
              placeholder="Button text"
            />
            <Input
              data-testid={`cta-button-url-${block.id}`}
              value={block.props.button_url}
              onChange={(e) => onUpdate({ ...block.props, button_url: e.target.value })}
              placeholder="Button URL (optional)"
            />
            
            {/* Minimum view time */}
            <div className="border-t pt-3">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Minimum view time (seconds)
              </label>
              <Input
                type="number"
                min="0"
                value={block.props.min_view_seconds || 0}
                onChange={(e) => onUpdate({ ...block.props, min_view_seconds: parseInt(e.target.value) || 0 })}
                placeholder="0 = no restriction"
                className="w-32"
              />
              <p className="text-xs text-gray-500 mt-1">
                Learner must wait this many seconds before clicking &quot;Next&quot;
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card data-testid={`block-${block.id}`}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Badge>{block.type}</Badge>
            <span className="text-sm text-gray-500">Block {index + 1}</span>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMove(block.id, 'up')}
              disabled={!canMoveUp}
              data-testid={`move-up-${block.id}`}
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMove(block.id, 'down')}
              disabled={!canMoveDown}
              data-testid={`move-down-${block.id}`}
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              data-testid={`delete-${block.id}`}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
        {renderEditor()}
      </CardContent>
    </Card>
  );
}
